import { FC, useMemo } from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import countries from "../../../../data/countries.json";

type CountryMultiSelectSortableProps = {
  label?: string;
  name?: string;
  value: string[];
  onChange: (value: string[]) => void;
  [x: string]: any;
};

export const CountryMultiSelectSortable: FC<
  CountryMultiSelectSortableProps
> = ({ label = "Countries", name = "countries", value, onChange, ...rest }) => {
  const selectedOptions = useMemo(() => {
    const codes = value || [];
    // Preserve the order defined by `value`
    return codes
      .map((code) => countries.find((option) => option.code3 === code))
      .filter((x): x is NonNullable<typeof x> => Boolean(x));
  }, [value]);

  return (
    <Autocomplete
      multiple
      options={countries}
      value={selectedOptions}
      onChange={(_, newValue) => onChange(newValue.map((o) => o.code3))}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(o, v) => o.code3 === v.code3}
      filterSelectedOptions
      renderTags={(tagValue, getTagProps) => (
        <div
          onMouseDownCapture={(e) => {
            // Block Autocomplete root from calling preventDefault on mousedown
            e.stopPropagation();
          }}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            const fromIndexString = e.dataTransfer.getData("text/plain");
            const from = fromIndexString ? Number(fromIndexString) : null;
            if (from === null || Number.isNaN(from)) return;
            const currentCodes = tagValue.map((t) => t.code3);
            const nextCodes = [...currentCodes];
            const [moved] = nextCodes.splice(from, 1);
            nextCodes.push(moved);
            onChange(nextCodes);
          }}
          style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
        >
          {tagValue.map((option, index) => {
            const chipProps = getTagProps({ index });

            const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
              event.preventDefault();
              const fromIndexString = event.dataTransfer.getData("text/plain");
              const from = fromIndexString ? Number(fromIndexString) : null;
              const to = index;
              if (from === null || Number.isNaN(from) || from === to) return;

              event.stopPropagation();

              const currentCodes = tagValue.map((t) => t.code3);
              const nextCodes = [...currentCodes];
              const [moved] = nextCodes.splice(from, 1);
              nextCodes.splice(to, 0, moved);
              onChange(nextCodes);
            };

            return (
              <Chip
                {...chipProps}
                label={option.name}
                size="small"
                icon={<SwapHorizIcon fontSize="small" />}
                sx={{ cursor: "grab" }}
                key={option.code3}
                draggable
                onMouseDownCapture={(e) => {
                  // Stop Autocomplete's internal mousedown preventDefault from firing
                  e.stopPropagation();
                }}
                onDragStart={(event) => {
                  event.dataTransfer.effectAllowed = "move";
                  try {
                    event.dataTransfer.setData("text/plain", String(index));
                  } catch {}
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                }}
                onDrop={handleDrop}
                style={{ display: "inline-flex" }}
              />
            );
          })}
        </div>
      )}
      renderInput={(params) => (
        <TextField {...params} label={label} variant="filled" name={name} />
      )}
      {...rest}
    />
  );
};
